/**
 * Nosh PDP V2 — product.nosh-v2 behaviour  (Pass 3.2 — final UX completion)
 * ---------------------------------------------------------------------------
 * Loaded ONLY from snippets/nosh-sticky-atc.liquid, which is rendered only by
 * templates/product.nosh-v2.json. It never runs on the default product
 * template, on any other template, or in the Refresh theme.
 *
 * Responsibilities (deliberately small — no library, no framework):
 *   1. Mobile sticky Add to Cart that PROXIES Dawn's canonical submit button.
 *      It never creates a form, never touches the Cart API, never implements
 *      checkout logic, and never touches the accelerated-checkout button.
 *   2. Suppression rules so the bar can never obstruct the cart drawer, the
 *      media lightbox, a modal, the preparation-video modal, or the footer —
 *      plus temporary mobile-only content clearance so nothing ends up
 *      permanently unreachable underneath it.
 *   3. Optional preparation video: gutter-aware desktop launcher, muted
 *      looping previews with viewport-aware playback, and a native <dialog>
 *      modal. Inert until a merchant assigns a preparation guide.
 *   4. Releases the related-rail height reservation if recommendations never
 *      arrive, so no empty space is left behind.
 *
 * PASS 3.1 (retained): the reveal is a forced synchronous reflow rather than
 * requestAnimationFrame, and visibility is driven by direct
 * getBoundingClientRect() geometry on scroll/resize plus a low-frequency
 * interval safety net — no IntersectionObserver and no broad MutationObserver
 * in the sticky path, both of which were proven to miss the initial callback
 * in this theme.
 *
 * PASS 3.2 CHANGES
 * ----------------
 * A. Sticky timing extended. The bar used to disappear as soon as ANY pixel
 *    of the footer entered the viewport, which cut it off early — well before
 *    the reviews and often around the recommendation carousel. It now hides
 *    only once the footer is genuinely about to reach the bar:
 *        footerTop < innerHeight - (barHeight + breathing room)
 *    where barHeight already includes the safe-area inset (the bar's
 *    padding-bottom uses env(safe-area-inset-bottom)). Nothing else keys off
 *    the carousel, the reviews or the accordions, so the bar simply stays up
 *    through all of them.
 * B. The footer lookup is now restricted to the real footer section group, so
 *    an app widget that happens to use a `.footer` class can no longer be
 *    mistaken for the page footer and hide the bar early.
 * C. Mobile-only content clearance while the bar is visible, removed when it
 *    hides — no permanent blank space, no layout shift at first paint.
 * D. Preparation launcher is placed by MEASURED gutter, not by media query,
 *    and falls back to the inline card rather than overlapping the text.
 * E. Muted looping previews replace the static poster + blue play badge, with
 *    viewport-, tab- and modal-aware playback and reduced-motion respect.
 *
 * Idempotent by design: Theme Editor section reloads re-inject this script,
 * so every run tears down the previous run's observers/listeners first and
 * guarantees exactly one sticky bar in the document.
 */
(function () {
  'use strict';

  var NS = (window.NoshPDP = window.NoshPDP || {});

  var CANONICAL_BUTTON = '.product-form__buttons [id^="ProductSubmitButton-"]';
  var INFO_CONTAINER = '.product__info-container';

  /* Real footer section group only. Deliberately NOT a bare `.footer` — an
     app widget using that class earlier in the DOM would hide the bar early. */
  var FOOTER_PRIMARY = '.shopify-section-group-footer-group, #shopify-section-footer';
  var FOOTER_FALLBACK = 'footer.footer';

  /* Breathing space between the footer and the top of the sticky bar. */
  var FOOTER_BREATHING = 20;

  /* Desktop preparation launcher geometry (px resolved from rem at runtime). */
  var LAUNCHER_REM = 9.5; /* matches .nosh-prep__float width in nosh-pdp.css */
  var LAUNCHER_RIGHT = 24; /* right-edge spacing, within the approved 20-32px */
  var LAUNCHER_BREATHING = 24; /* clearance from the content container (page_width=1200) */
  var DESKTOP_QUERY = '(min-width: 990px)';

  function noop() {}

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function teardown() {
    if (!NS.cleanup) return;
    NS.cleanup.forEach(function (fn) {
      try {
        fn();
      } catch (e) {
        /* observer/listener already gone */
      }
    });
    NS.cleanup = [];
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Any full-screen overlay Dawn or this theme can open. Dawn adds
     `overflow-hidden` to <body> for the cart drawer (assets/cart-drawer.js)
     and every modal-dialog including the product media modal
     (assets/global.js ModalDialog.show()) — confirmed against the live
     source, not assumed. `dialog[open]` covers the preparation modal. */
  function overlayOpen() {
    return document.body.classList.contains('overflow-hidden') || !!document.querySelector('dialog[open]');
  }

  function footerElements() {
    var els = document.querySelectorAll(FOOTER_PRIMARY);
    if (!els.length) els = document.querySelectorAll(FOOTER_FALLBACK);
    return els;
  }

  function footerTop() {
    var els = footerElements();
    var top = Infinity;
    for (var i = 0; i < els.length; i++) {
      var t = els[i].getBoundingClientRect().top;
      if (t < top) top = t;
    }
    return top;
  }

  function rootFontSize() {
    var size = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    return size > 0 ? size : 10;
  }

  /* ----------------------------------------------------------------------
     1. Mobile sticky Add to Cart
     ---------------------------------------------------------------------- */
  function initStickyAtc() {
    // Exactly one bar, always. A Theme Editor reload leaves the previous bar
    // parked on <body> while a fresh one renders inside the info column.
    var bars = document.querySelectorAll('[data-nosh-sticky-atc]');
    if (!bars.length) return;
    var bar = bars[bars.length - 1];
    for (var i = 0; i < bars.length - 1; i++) bars[i].remove();

    var info = document.querySelector(INFO_CONTAINER);
    if (!info) return;

    // Escape any transformed ancestor (Dawn puts `transform` on
    // .scroll-trigger.animate--slide-in, which would otherwise become the
    // containing block for position:fixed) and land last in tab order.
    if (bar.parentNode !== document.body) document.body.appendChild(bar);

    var proxy = bar.querySelector('[data-nosh-satc-button]');
    var labelEl = bar.querySelector('[data-nosh-satc-label]');
    var priceEl = bar.querySelector('[data-nosh-satc-price]');
    if (!proxy || !labelEl) return;

    var visible = false;
    var pastButtons = false;
    var footerNear = false;
    var barHeight = 64; // sane pre-measurement default; corrected on first reveal

    function canonicalButton() {
      return info.querySelector(CANONICAL_BUTTON);
    }

    /* Mobile-only clearance so the last stretch of the page is never
       permanently unreachable under the fixed bar. Applied ONLY while the bar
       is on screen and removed the moment it hides, so there is no permanent
       blank space and nothing shifts at first paint. */
    function applyClearance(on) {
      if (on) {
        document.body.style.setProperty('--nosh-satc-h', barHeight + 'px');
        document.body.setAttribute('data-nosh-satc-clear', '');
      } else {
        document.body.removeAttribute('data-nosh-satc-clear');
        document.body.style.removeProperty('--nosh-satc-h');
      }
    }

    // Reveal/hide via a forced synchronous reflow rather than
    // requestAnimationFrame. void bar.offsetHeight commits the
    // "hidden removed, still translateY(115%)" state before the transform
    // is changed, so the CSS transition still animates — without depending
    // on a rAF callback that is not guaranteed to run promptly in every
    // rendering context.
    function reveal(next) {
      if (next === visible) return;
      visible = next;
      if (next) {
        bar.hidden = false;
        void bar.offsetHeight;
        // offsetHeight already includes the safe-area inset, because the bar's
        // padding-bottom is calc(0.7rem + env(safe-area-inset-bottom)).
        if (bar.offsetHeight > 0) barHeight = bar.offsetHeight;
        bar.removeAttribute('aria-hidden');
        bar.setAttribute('data-nosh-visible', '');
        applyClearance(true);
      } else {
        bar.removeAttribute('data-nosh-visible');
        bar.setAttribute('aria-hidden', 'true');
        applyClearance(false);
        window.setTimeout(function () {
          if (!visible) bar.hidden = true;
        }, 200);
      }
    }

    function evaluate() {
      reveal(pastButtons && !footerNear && !overlayOpen());
    }

    function syncState() {
      var canonical = canonicalButton();
      if (!canonical) return;

      var text = canonical.querySelector('span');
      if (text && text.textContent.trim()) labelEl.textContent = text.textContent.trim();

      var isDisabled = canonical.hasAttribute('disabled') || canonical.getAttribute('aria-disabled') === 'true';
      if (isDisabled) {
        proxy.setAttribute('disabled', '');
      } else {
        proxy.removeAttribute('disabled');
      }

      if (priceEl) {
        var price =
          info.querySelector('.price__container .price-item--sale, .price__container .price-item--regular') ||
          info.querySelector('.price .price-item--sale, .price .price-item--regular');
        if (price && price.textContent.trim()) priceEl.textContent = price.textContent.trim();
      }
    }

    // Primary trigger: direct geometry measurement, rAF-throttled on
    // scroll/resize, plus a low-frequency interval as a safety net for any
    // environment where scroll/resize somehow don't dispatch (e.g. a
    // programmatic scroll performed without native events).
    var ticking = false;

    function measure() {
      ticking = false;
      syncState();

      var canonical = canonicalButton();
      pastButtons = canonical ? canonical.getBoundingClientRect().bottom < 0 : false;

      // PASS 3.2: hide only once the footer is about to reach the bar, using
      // the measured bar height (safe-area inset included) plus breathing
      // space — not the moment the footer's first pixel appears. Everything
      // between the purchase controls and that point — accordions, Snack
      // Passport, Pair it with, You may also like, reviews — keeps the bar.
      var limit = window.innerHeight - (barHeight + FOOTER_BREATHING);
      footerNear = footerTop() < limit;

      evaluate();
    }

    function requestMeasure() {
      if (ticking) return;
      ticking = true;
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(measure);
      } else {
        window.setTimeout(measure, 66);
      }
    }

    window.addEventListener('scroll', requestMeasure, { passive: true });
    window.addEventListener('resize', requestMeasure);
    var pollId = window.setInterval(measure, 400);

    function onProxyClick(event) {
      event.preventDefault();
      var canonical = canonicalButton();
      if (!canonical || canonical.hasAttribute('disabled')) return;
      // Hand straight back to Dawn: validation, spinner, errors, cart drawer.
      canonical.click();
    }
    proxy.addEventListener('click', onProxyClick);

    // Dawn's documented signal that the buy-buttons DOM was swapped
    // (variant change). Re-sync immediately rather than waiting for the
    // next poll/scroll tick.
    var variantUnsubscribe = null;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, function () {
        measure();
      });
    }

    // Overlay open/close is a single, precisely-targeted <body> class
    // change — a narrow, cheap observer.
    var bodyObserver = null;
    if ('MutationObserver' in window) {
      bodyObserver = new MutationObserver(evaluate);
      bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    NS.cleanup.push(function () {
      window.removeEventListener('scroll', requestMeasure);
      window.removeEventListener('resize', requestMeasure);
      window.clearInterval(pollId);
      if (bodyObserver) bodyObserver.disconnect();
      if (variantUnsubscribe) variantUnsubscribe();
      proxy.removeEventListener('click', onProxyClick);
      applyClearance(false);
    });

    measure();
  }

  /* ----------------------------------------------------------------------
     2. Related-products rail — release the height reservation
     The rail reserves one card row so the async recommendation fetch does not
     shift the page. If Shopify returns nothing, release it so no empty space
     is left behind. Once cards arrive, CSS :has() has already released it.
     ---------------------------------------------------------------------- */
  function initRelatedRail() {
    var rail = document.querySelector('.nosh-rrail');
    if (!rail) return;
    var timer = window.setTimeout(function () {
      rail.setAttribute('data-nosh-rrail-settled', '');
    }, 6000);
    NS.cleanup.push(function () {
      window.clearTimeout(timer);
    });
  }

  /* ----------------------------------------------------------------------
     3. Optional preparation video
     ---------------------------------------------------------------------- */
  function initPrepVideo() {
    var root = document.querySelector('[data-nosh-prep]');
    if (!root) return; // no guide assigned -> nothing rendered -> nothing to do

    var dialog = root.querySelector('dialog');
    var modalVideo = root.querySelector('[data-nosh-prep-video]');
    var floatEl = root.querySelector('[data-nosh-prep-float]');
    var dismissEl = root.querySelector('[data-nosh-prep-dismiss]');
    var previews = Array.prototype.slice.call(root.querySelectorAll('[data-nosh-prep-preview]'));
    var storageKey = 'nosh-prep-dismissed';

    var modalOpen = false;
    var dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(storageKey) === '1';
    } catch (e) {
      dismissed = false;
    }

    /* ---- muted looping previews -------------------------------------- */
    /* No `autoplay` attribute is used. Playback starts only when a preview is
       meaningfully visible, the tab is foregrounded, no modal is open and the
       visitor has not asked for reduced motion. Under reduced motion the
       poster / first frame is shown and never animates. */
    function meaningfullyVisible(el) {
      if (!el.offsetParent && el.offsetWidth === 0 && el.offsetHeight === 0) return false;
      var r = el.getBoundingClientRect();
      if (r.height === 0) return false;
      var top = Math.max(r.top, 0);
      var bottom = Math.min(r.bottom, window.innerHeight);
      return bottom - top >= r.height * 0.5;
    }

    function syncPreviews() {
      var allowMotion = !prefersReducedMotion() && !document.hidden && !modalOpen;
      previews.forEach(function (v) {
        var shouldPlay = allowMotion && meaningfullyVisible(v);
        if (shouldPlay) {
          if (v.paused) {
            v.muted = true;
            var attempt = v.play();
            if (attempt && typeof attempt.catch === 'function') attempt.catch(noop);
          }
        } else if (!v.paused) {
          v.pause();
        }
      });
    }

    /* ---- desktop gutter-aware placement ------------------------------- */
    /* The launcher only appears when the viewport genuinely has enough empty
       space OUTSIDE the main content container. It is never merely shrunk on
       top of the product information column — when the gutter is too narrow
       the inline card is used instead. */
    function contentContainer() {
      var info = document.querySelector(INFO_CONTAINER);
      var container = info && info.closest ? info.closest('.page-width') : null;
      return container || document.querySelector('#MainContent .page-width');
    }

    function launcherFits() {
      if (!floatEl) return false;
      if (dismissed) return false;
      if (!window.matchMedia(DESKTOP_QUERY).matches) return false;

      var container = contentContainer();
      if (!container) return false;

      var rect = container.getBoundingClientRect();
      var gutter = window.innerWidth - rect.right;
      var needed = LAUNCHER_REM * rootFontSize() + LAUNCHER_RIGHT + LAUNCHER_BREATHING;
      return gutter >= needed;
    }

    function syncLauncher() {
      if (!floatEl) return;

      var show = launcherFits();

      if (show) {
        // Hide near the footer and behind any overlay, exactly like the bar.
        var limit = window.innerHeight - FOOTER_BREATHING;
        if (footerTop() < limit) show = false;
        if (overlayOpen()) show = false;
        // Only reveal after meaningful scroll, as before.
        if (window.scrollY <= window.innerHeight * 0.4) show = false;
      }

      if (show) {
        floatEl.style.setProperty('--nosh-prep-right', LAUNCHER_RIGHT + 'px');
        floatEl.hidden = false;
        root.setAttribute('data-nosh-prep-floating', '');
      } else {
        floatEl.hidden = true;
        root.removeAttribute('data-nosh-prep-floating');
      }
    }

    /* ---- modal -------------------------------------------------------- */
    function open() {
      if (!dialog) return;
      modalOpen = true;
      syncPreviews(); // pause the small previews — no duplicate motion or audio
      if (typeof dialog.showModal === 'function') {
        dialog.showModal(); // native focus trap + Escape + focus restoration
      } else {
        dialog.setAttribute('open', '');
      }
      syncLauncher(); // the launcher must not sit on top of the modal
      if (modalVideo) {
        var attempt = modalVideo.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(noop);
      }
    }

    function close() {
      if (modalVideo) {
        modalVideo.pause();
        modalVideo.currentTime = 0;
      }
      modalOpen = false;
      if (dialog) {
        if (typeof dialog.close === 'function') {
          if (dialog.open) dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
      }
      syncPreviews();
      syncLauncher();
    }

    var openers = Array.prototype.slice.call(root.querySelectorAll('[data-nosh-prep-open]'));
    var closers = Array.prototype.slice.call(root.querySelectorAll('[data-nosh-prep-close]'));
    openers.forEach(function (el) {
      el.addEventListener('click', open);
    });
    closers.forEach(function (el) {
      el.addEventListener('click', close);
    });
    if (dialog) dialog.addEventListener('close', close);

    function onDismiss() {
      dismissed = true;
      syncLauncher();
      try {
        window.sessionStorage.setItem(storageKey, '1');
      } catch (e) {
        /* storage unavailable — dismissal is simply not remembered */
      }
    }
    if (dismissEl) dismissEl.addEventListener('click', onDismiss);

    /* ---- drivers ------------------------------------------------------ */
    var prepTicking = false;
    function prepMeasure() {
      prepTicking = false;
      syncLauncher();
      syncPreviews();
    }
    function requestPrepMeasure() {
      if (prepTicking) return;
      prepTicking = true;
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(prepMeasure);
      } else {
        window.setTimeout(prepMeasure, 66);
      }
    }

    window.addEventListener('scroll', requestPrepMeasure, { passive: true });
    window.addEventListener('resize', requestPrepMeasure);
    document.addEventListener('visibilitychange', syncPreviews);

    /* An IntersectionObserver keeps off-screen previews from decoding without
       relying on scroll events alone; it is disconnected on teardown. */
    var previewObserver = null;
    if ('IntersectionObserver' in window && previews.length) {
      previewObserver = new IntersectionObserver(syncPreviews, { threshold: [0, 0.5, 1] });
      previews.forEach(function (v) {
        previewObserver.observe(v);
      });
    }

    var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    function onMotionChange() {
      syncPreviews();
    }
    if (motionQuery.addEventListener) {
      motionQuery.addEventListener('change', onMotionChange);
    } else if (motionQuery.addListener) {
      motionQuery.addListener(onMotionChange);
    }

    NS.cleanup.push(function () {
      window.removeEventListener('scroll', requestPrepMeasure);
      window.removeEventListener('resize', requestPrepMeasure);
      document.removeEventListener('visibilitychange', syncPreviews);
      if (previewObserver) previewObserver.disconnect();
      if (motionQuery.removeEventListener) {
        motionQuery.removeEventListener('change', onMotionChange);
      } else if (motionQuery.removeListener) {
        motionQuery.removeListener(onMotionChange);
      }
      openers.forEach(function (el) {
        el.removeEventListener('click', open);
      });
      closers.forEach(function (el) {
        el.removeEventListener('click', close);
      });
      if (dismissEl) dismissEl.removeEventListener('click', onDismiss);
      previews.forEach(function (v) {
        v.pause();
      });
    });

    prepMeasure();
  }

  function init() {
    teardown();
    NS.cleanup = [];
    initStickyAtc();
    initRelatedRail();
    initPrepVideo();
  }

  if (!NS.bound) {
    NS.bound = true;
    NS.cleanup = [];
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:unload', teardown);
  }

  ready(init);
})();
